import { BracesIcon, XIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { PropertyType } from "@/api/types/list";
import { TypeIcon } from "@/components/ui/select-new";
import { cn } from "@/lib/utils";
import { resolveFlowFieldMeta } from "./hooks/use-flow-fields";

/**
 * The single visual primitive for an inserted dynamic field, reused everywhere a
 * flow surfaces one: inline merge-tag chips in message bodies, webhook payload
 * values, condition values, and the timer date. Keeps the curated label + a
 * type icon so it reads the same as the segment chips.
 *
 * Pass `label` (resolved via `useFlowFieldOptions().resolveLabel`) when you have
 * it; otherwise the chip resolves a label from `path` using the curated
 * taxonomy fallback.
 */
export function DynamicFieldChip({
  path,
  label,
  type,
  onRemove,
  trailing,
  className,
}: {
  path?: string;
  label?: string;
  type?: PropertyType;
  onRemove?: () => void;
  /** Optional trailing content rendered inside the pill (e.g. an info icon). */
  trailing?: ReactNode;
  className?: string;
}) {
  const resolvedLabel = label ?? (path ? resolveFlowFieldMeta(path).label : "");

  return (
    <span
      className={cn(
        // Transparent fill so the chip blends into whatever surface it sits on
        // (white email body, tinted subject header, message bubble, …) — the
        // border alone defines the pill. Callers can override via `className`.
        "inline-flex items-center gap-1 rounded-md border bg-transparent px-1.5 py-0.5 align-baseline text-xs font-medium text-foreground",
        className,
      )}
      data-dynamic-field-path={path}
    >
      {type ? (
        <TypeIcon type={type} className="mr-0 hidden" />
      ) : (
        <BracesIcon className="text-muted-foreground size-3 shrink-0 hidden" aria-hidden />
      )}
      <span className="truncate">{resolvedLabel}</span>
      {trailing}
      {onRemove ? (
        <button
          type="button"
          aria-label="Remove field"
          onClick={(event) => {
            // Chips can sit inside a clickable popover trigger; removing the
            // field must not also toggle that popover.
            event.stopPropagation();
            onRemove();
          }}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <XIcon className="size-3" aria-hidden />
        </button>
      ) : null}
    </span>
  );
}
